import { apiDateTimeToIsoLocal } from './portalDateFormat'

/** Respuesta Geclisa tipo <c>ResponseAPI&lt;List&lt;TurnosPendientesDTO&gt;&gt;</c> o arreglo directo. */
export function extractTurnosListFromApi(data) {
  if (data == null) return []
  const inner = data.data
  if (Array.isArray(inner)) return inner
  if (Array.isArray(data)) return data
  return []
}

function stripHtmlBasic(s) {
  return String(s ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** HsIni Geclisa: minutos desde medianoche o HHmm (p. ej. 1030). */
export function formatHsIniDisplay(hsIni) {
  const n = Number(hsIni)
  if (!Number.isFinite(n) || n < 0) return '—'
  if (n <= 24 * 60) {
    const h = Math.floor(n / 60)
    const m = n % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} hs`
  }
  const h = Math.floor(n / 100)
  const m = n % 100
  if (m >= 60) return '—'
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} hs`
}

export function formatTurnoFechaCorta(value) {
  const iso = apiDateTimeToIsoLocal(value)
  if (!iso) return '—'
  const [y, mo, d] = iso.split('-').map(Number)
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return '—'
  const date = new Date(y, mo - 1, d)
  return date.toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function formatTurnoHoraDesdeApi(raw) {
  const fechaRaw = raw?.fecha
  if (fechaRaw != null && String(fechaRaw).trim() !== '') {
    const d = new Date(fechaRaw)
    if (!Number.isNaN(d.getTime())) {
      const h = d.getHours()
      const mi = d.getMinutes()
      return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')} hs`
    }
  }
  return formatHsIniDisplay(raw?.hsIni)
}

export function getTurnoFechaOrdenMs(raw) {
  const fechaRaw = raw?.fecha
  if (fechaRaw != null && String(fechaRaw).trim() !== '') {
    const d = new Date(fechaRaw)
    if (!Number.isNaN(d.getTime())) return d.getTime()
  }

  const iso = apiDateTimeToIsoLocal(fechaRaw)
  if (!iso) return Number.MAX_SAFE_INTEGER

  const [y, mo, d] = iso.split('-').map(Number)
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
    return Number.MAX_SAFE_INTEGER
  }

  const hsIni = Number(raw?.hsIni)
  let h = 0
  let mi = 0
  if (Number.isFinite(hsIni) && hsIni >= 0) {
    if (hsIni <= 24 * 60) {
      h = Math.floor(hsIni / 60)
      mi = hsIni % 60
    } else {
      h = Math.floor(hsIni / 100)
      mi = hsIni % 100
    }
  }
  return new Date(y, mo - 1, d, h, mi, 0, 0).getTime()
}

export function formatOsPlanDisplay(raw) {
  const v = raw?.osPlan
  if (v == null) return ''
  if (typeof v === 'string') {
    const s = String(v).trim()
    return s
  }
  if (typeof v === 'object') {
    const os = String(v.obraSocial ?? v.osNombre ?? '').trim()
    const pl = String(v.plan ?? v.planNombre ?? '').trim()
    if (os && pl) return `${os} — ${pl}`
    return os || pl
  }
  return String(v).trim()
}

export function mapTurnoPacienteRow(raw, index, options = {}) {
  const esHistorial = Boolean(options.esHistorial)
  const turnoId = Number(raw?.turnoId ?? index)
  const fichaId = Number(raw?.fichaId ?? raw?.FichaId)
  const nomId = Number(raw?.nomId ?? raw?.NomId)
  const servId = Number(raw?.servId ?? raw?.ServId)
  const nomCod = String(raw?.nomCod ?? raw?.NomCod ?? '').trim()
  const servicioNombre = String(raw?.servicioNombre ?? '').trim()
  const nombrePrestador = String(raw?.nombrePrestador ?? '').trim()
  const cancelado = Boolean(raw?.cancelado)
  const confirmado = Boolean(raw?.confirmado)
  const asistio = Boolean(raw?.asistio)
  const esTurnoFamiliar = Boolean(raw?.esTurnoFamiliar ?? raw?.EsTurnoFamiliar)
  const esVideoconsulta = Boolean(raw?.esVideoConsulta ?? raw?.esVideoconsulta)
  const pacienteNombre = String(raw?.nombre ?? raw?.Nombre ?? '').trim()
  const pacienteApellido = String(raw?.apellido ?? raw?.Apellido ?? '').trim()
  const pacienteDisplay = `${pacienteNombre} ${pacienteApellido}`.trim()
    || String(raw?.paciente ?? raw?.Paciente ?? '').trim()
  const ubicacion = String(raw?.ubicacion ?? '').trim()
  const lugar = esVideoconsulta
    ? 'VIDEOCONSULTA'
    : (ubicacion ? ubicacion.toUpperCase() : '—')

  const observaciones = stripHtmlBasic(raw?.observaciones ?? '')

  const fechaDisplay = formatTurnoFechaCorta(raw?.fecha)
  const horaDisplay = formatTurnoHoraDesdeApi(raw)
  const fechaOrdenMs = getTurnoFechaOrdenMs(raw)
  const osPlanRaw = String(formatOsPlanDisplay(raw) ?? '').trim()
  const osPlanDisplay = osPlanRaw ? osPlanRaw.toUpperCase() : '—'

  let estadoLabel
  if (cancelado) {
    estadoLabel = 'Cancelado'
  } else if (esHistorial) {
    estadoLabel = asistio ? 'Asistió' : 'No asistió'
  } else {
    estadoLabel = confirmado ? 'Confirmado' : 'Sin confirmar'
  }

  return {
    id: String(Number.isFinite(turnoId) ? turnoId : index),
    turnoId: Number.isFinite(turnoId) ? turnoId : index,
    fichaId: Number.isFinite(fichaId) && fichaId > 0 ? fichaId : null,
    nomId: Number.isFinite(nomId) ? nomId : 0,
    nomCod,
    servId: Number.isFinite(servId) ? servId : 0,
    puedeConsultarPreparacion:
      Number.isFinite(turnoId) &&
      turnoId > 0 &&
      !cancelado &&
      !esHistorial,
    medico: nombrePrestador ? nombrePrestador.toUpperCase() : '—',
    especialidad: servicioNombre ? servicioNombre.toUpperCase() : '-',
    esTurnoFamiliar,
    pacienteDisplay: pacienteDisplay ? pacienteDisplay.toUpperCase() : '',
    fechaDisplay,
    fechaOrdenMs,
    horaDisplay,
    lugar,
    esVideoconsulta,
    osPlanDisplay,
    cancelado,
    confirmado,
    asistio,
    esHistorial,
    estadoLabel,
    aviso: observaciones,
  }
}
